import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { types as api } from '@mesa-labs/mesa-api';
import { VendorApi } from '@mesa-labs/mesa-api/dist/apis/vendor';

import { prepareHeaders, useApi } from './api';

export const permissionsApi = createApi({
  reducerPath: 'permissionsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: CONFIG.api.vendorUrl,
    prepareHeaders,
  }),
  tagTypes: ['Permissions', 'PermissionGroups', 'PermissionUsers'],
  endpoints: (build) => ({
    getPermissions: build.query<readonly api.PermissionResponse[], void>({
      async queryFn() {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.getPermissions();
        return { data };
      },
      providesTags: ['Permissions'],
    }),
    getPermissionGroups: build.query<readonly api.PermissionGroupResponse[], void>({
      async queryFn() {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.getPermissionGroups();
        return { data };
      },
      providesTags: ['PermissionGroups'],
    }),
    getPermissionUsers: build.query<api.IPagedResults<api.PermissionUserResponse>, api.PermissionUsersFilterParams>({
      async queryFn(params) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.getPermissionUsers(params);
        return { data };
      },
      providesTags: (result) => [
        'PermissionUsers',
        ...(result?.data || []).map((user) => ({ type: 'PermissionUsers' as const, id: user.email })),
      ],
    }),
    getPermissionUser: build.query<api.PermissionUserResponse, string>({
      async queryFn(email) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.getPermissionUser(email);
        return { data };
      },
      providesTags: (result, error, email) => [{ type: 'PermissionUsers', id: email }],
    }),
    putUserPermissionGroup: build.mutation<api.PermissionUserResponse, api.PutUserPermissionGroupRequest & { email: string; group: string; }>({
      async queryFn({ email, group, ...request }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.putUserPermissionGroup(email, group, request);
        return { data };
      },
      invalidatesTags: (result, error, { email }) => ['PermissionUsers', { type: 'PermissionUsers', id: email }],
    }),
    deleteUserPermissionGroup: build.mutation<api.PermissionUserResponse, { email: string; group: string }>({
      async queryFn({ email, group }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.deleteUserPermissionGroup(email, group);
        return { data };
      },
      invalidatesTags: (result, error, { email }) => ['PermissionUsers', { type: 'PermissionUsers', id: email }],
    }),
    putUserPermission: build.mutation<api.PermissionUserResponse, api.PutUserPermissionRequest & { email: string; permission: string; }>({
      async queryFn({ email, permission, ...request }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.putUserPermission(email, permission, request);
        return { data };
      },
      invalidatesTags: (result, error, { email }) => ['PermissionUsers', { type: 'PermissionUsers', id: email }],
    }),
    deleteUserPermission: build.mutation<api.PermissionUserResponse, { email: string; permission: string }>({
      async queryFn({ email, permission }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.deleteUserPermission(email, permission);
        return { data };
      },
      invalidatesTags: (result, error, { email }) => ['PermissionUsers', { type: 'PermissionUsers', id: email }],
    }),
    createPermissionUser: build.mutation<api.PermissionUserResponse, api.CreatePermissionUserRequest>({
      async queryFn(request) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.createPermissionUser(request);
        return { data };
      },
      invalidatesTags: ['PermissionUsers'],
    }),
    deactivatePermissionUser: build.mutation<void, { email: string } & api.DeactivatePermissionUserRequest>({
      async queryFn({ email, ...request }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        await externalVendorApi.deactivatePermissionUser(email, request);
        return { data: undefined };
      },
      invalidatesTags: (result, error, { email }) => ['PermissionUsers', { type: 'PermissionUsers', id: email }],
    }),
  }),
});

export const {
  useGetPermissionsQuery,
  useGetPermissionGroupsQuery,
  useGetPermissionUsersQuery,
  useGetPermissionUserQuery,
  usePutUserPermissionGroupMutation,
  useDeleteUserPermissionGroupMutation,
  usePutUserPermissionMutation,
  useDeleteUserPermissionMutation,
  useCreatePermissionUserMutation,
  useDeactivatePermissionUserMutation,
} = permissionsApi;
